import type { NamingProject } from '../store/sessionStore';

function escapeMarkdown(text: string): string {
  return text.replace(/[\\`*_{}[\]()#+\-.!]/g, '\\$&');
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0];
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function generateCollabradorMarkdown(project: NamingProject): string {
  const timestamp = new Date();
  const slug = slugify(project.name);

  const sections: string[] = [];

  // Title block
  sections.push(`# ${project.name}`);
  sections.push('');
  sections.push('## Metadata');
  sections.push('');
  sections.push(`- **Project ID**: ${project.id}`);
  sections.push(`- **Export Date**: ${formatDate(timestamp)}`);
  sections.push(`- **Tool**: Namenym (AI-assisted naming workbench)`);
  sections.push('');

  // Project brief
  sections.push('## Project Brief');
  sections.push('');
  sections.push(project.brief || '_No brief provided_');
  sections.push('');

  // Selected naming styles
  if (project.selectedStyles && project.selectedStyles.length > 0) {
    sections.push('## Selected Naming Styles');
    sections.push('');
    project.selectedStyles.forEach(style => {
      sections.push(`- ${style}`);
    });
    sections.push('');
  }

  // Concept summary
  const sortedConcepts = [...project.concepts].sort((a, b) => b.votes - a.votes);
  if (sortedConcepts.length > 0) {
    sections.push('## Concept Summary');
    sections.push('');
    sections.push('Core naming concepts identified through brainstorming:');
    sections.push('');
    sections.push('| Concept | Votes | Source |');
    sections.push('|---------|-------|--------|');
    sortedConcepts.forEach(concept => {
      sections.push(`| ${concept.label} | ${concept.votes} | ${concept.source} |`);
    });
    sections.push('');
  }

  // Synonym highlights
  if (project.synonyms.length > 0) {
    sections.push('## Synonym Highlights');
    sections.push('');
    sections.push('Semantic variations and related terms for each concept:');
    sections.push('');

    sortedConcepts.forEach(concept => {
      const conceptSynonyms = project.synonyms
        .filter(syn => syn.conceptId === concept.id && !syn.dismissed)
        .sort((a, b) => b.votes - a.votes)
        .slice(0, 10);

      if (conceptSynonyms.length > 0) {
        const synonymList = conceptSynonyms
          .map(syn => {
            const voteIndicator = syn.votes > 0 ? ` (+${syn.votes})` : '';
            return `${syn.label}${voteIndicator} [${syn.source}]`;
          })
          .join(', ');
        sections.push(`**${concept.label}:** ${synonymList}`);
        sections.push('');
      }
    });
  }

  // Mashup overview
  if (project.mashups.length > 0) {
    const favoritedCount = project.shortlisted.length;
    const availableTlds = project.selectedTlds.join(', .');

    sections.push('## Mashup Overview');
    sections.push('');
    sections.push(`- **Total mashups generated**: ${project.mashups.length}`);
    sections.push(`- **Favorited**: ${favoritedCount}`);
    sections.push(`- **Checked TLDs**: .${availableTlds}`);
    sections.push('');
  }

  // Favorited mashups table
  const favoritedMashups = project.mashups.filter(m => project.shortlisted.includes(m.id));
  if (favoritedMashups.length > 0) {
    sections.push('## Favorited Names');
    sections.push('');
    sections.push('Names shortlisted for deeper evaluation:');
    sections.push('');

    // Check if any have scoring data
    const hasScoring = favoritedMashups.some(m => m.evaluation);

    if (hasScoring) {
      sections.push('| Name | Total Score | Domain Availability | Votes | Sources |');
      sections.push('|------|-------------|---------------------|-------|---------|');
    } else {
      sections.push('| Name | Domain Availability | Votes | Sources |');
      sections.push('|------|---------------------|-------|---------|');
    }

    favoritedMashups
      .sort((a, b) => {
        const scoreA = a.evaluation?.totalScore ?? 0;
        const scoreB = b.evaluation?.totalScore ?? 0;
        return scoreB - scoreA;
      })
      .forEach(mashup => {
        const domains = (mashup.availableDomains || [])
          .filter(d => project.selectedTlds.includes(d.tld))
          .map(d => `.${d.tld}`)
          .join(', ') || '_checking_';

        const sourceLabels = mashup.sources
          .map(src => {
            if (src.type === 'concept') {
              const concept = project.concepts.find(c => c.id === src.id);
              return concept ? concept.label : 'Unknown';
            } else {
              const synonym = project.synonyms.find(s => s.id === src.id);
              return synonym ? synonym.label : 'Unknown';
            }
          })
          .join(' + ');

        if (hasScoring) {
          const score = mashup.evaluation?.totalScore?.toFixed(1) ?? '—';
          sections.push(`| ${mashup.label} | ${score} | ${domains} | ${mashup.votes} | ${sourceLabels} |`);
        } else {
          sections.push(`| ${mashup.label} | ${domains} | ${mashup.votes} | ${sourceLabels} |`);
        }
      });
    sections.push('');

    // Add detailed scoring if available
    const scoredMashups = favoritedMashups.filter(m => m.evaluation);
    if (scoredMashups.length > 0) {
      sections.push('### Detailed Scoring Analysis');
      sections.push('');
      sections.push('AI-assisted evaluation based on naming best practices:');
      sections.push('');

      scoredMashups.forEach(mashup => {
        const evaluation = mashup.evaluation!;
        sections.push(`#### ${mashup.label} (Score: ${evaluation.totalScore.toFixed(1)}/100)`);
        sections.push('');

        sections.push('**Concept Fit**: ' + evaluation.scores.conceptFit.toFixed(1));
        sections.push('**Memorability**: ' + evaluation.scores.memorability.toFixed(1));
        sections.push('**Pronounceability**: ' + evaluation.scores.pronounceability.toFixed(1));
        sections.push('**Uniqueness**: ' + evaluation.scores.uniqueness.toFixed(1));
        sections.push('**Trademark Risk**: ' + evaluation.scores.trademarkRisk.toFixed(1));
        sections.push('**Domain Availability**: ' + evaluation.scores.domainAvailability.toFixed(1));
        sections.push('**SEO/Discoverability**: ' + evaluation.scores.searchDiscoverability.toFixed(1));
        sections.push('**Emotional Resonance**: ' + evaluation.scores.emotionalResonance.toFixed(1));
        sections.push('**Extendability**: ' + evaluation.scores.extendability.toFixed(1));
        sections.push('**Visual Adaptability**: ' + evaluation.scores.visualAdaptability.toFixed(1));
        sections.push('');

        if (evaluation.strengths.length > 0) {
          sections.push('**Strengths:**');
          sections.push('');
          evaluation.strengths.forEach(strength => {
            sections.push(`- ${strength}`);
          });
          sections.push('');
        }

        if (evaluation.risks.length > 0) {
          sections.push('**Risks:**');
          sections.push('');
          evaluation.risks.forEach(risk => {
            sections.push(`- ${risk}`);
          });
          sections.push('');
        }

        sections.push('**Notes:**');
        sections.push('');
        sections.push(evaluation.notes);
        sections.push('');
        sections.push('---');
        sections.push('');
      });
    }
  }

  // Name scoring framework reference
  sections.push('## Name Scoring Framework');
  sections.push('');
  sections.push('Names are evaluated across 10 dimensions grouped into three clusters:');
  sections.push('');
  sections.push('**Core Identity (45% weight):**');
  sections.push('- Concept & Tone Fit (15%)');
  sections.push('- Memorability (15%)');
  sections.push('- Pronounceability & Spellability (15%)');
  sections.push('');
  sections.push('**Practical Viability (35% weight):**');
  sections.push('- Uniqueness & Differentiation (12%)');
  sections.push('- Trademark & Legal Risk (10%)');
  sections.push('- Domain & Handle Availability (8%)');
  sections.push('- Search & Discoverability (5%)');
  sections.push('');
  sections.push('**Strategic Fit (20% weight):**');
  sections.push('- Emotional Resonance (8%)');
  sections.push('- Conceptual Extendability (6%)');
  sections.push('- Visual & Linguistic Adaptability (6%)');
  sections.push('');
  sections.push('Each dimension scored 0–5. Scores below 3 in core dimensions flag potential blockers.');
  sections.push('');

  // Agentic follow-up prompt for Collabrador
  sections.push('## Research Prompt for Collabrador');
  sections.push('');
  sections.push('You are an AI research assistant helping evaluate brand name candidates. Based on the favorited names above, please conduct the following research:');
  sections.push('');
  sections.push('### 1. Trademark & Legal Analysis');
  sections.push('- Search USPTO database for similar registered trademarks in relevant classes');
  sections.push('- Identify any exact or confusingly similar marks');
  sections.push('- Flag high-risk collisions with established brands');
  sections.push('- Note: International trademark databases (WIPO, EU IPO) if global expansion planned');
  sections.push('');
  sections.push('### 2. Competitive Landscape');
  sections.push('- Search for companies/products with identical or similar names');
  sections.push('- Identify the industries/sectors where similar names exist');
  sections.push('- Assess likelihood of customer confusion');
  sections.push('- Check Y Combinator, Crunchbase, Product Hunt for startup name conflicts');
  sections.push('');
  sections.push('### 3. SEO & Online Presence');
  sections.push('- Google search volume and competition for each name');
  sections.push('- Identify dominant search results (if name is taken by existing entity)');
  sections.push('- Check social media handle availability (Twitter, Instagram, LinkedIn, GitHub)');
  sections.push('- Assess difficulty of ranking for brand name + common product terms');
  sections.push('');
  sections.push('### 4. Domain Investigation');

  if (favoritedMashups.length > 0) {
    sections.push('- Verify current domain availability status');
    sections.push('- For taken domains: identify current owner, usage, and purchase potential');
    sections.push('- Check domain history (WHOIS, archive.org) for reputation issues');
    sections.push('- Suggest alternative TLDs if primary choices unavailable');
  } else {
    sections.push('- Check domain availability across .com, .io, .ai, .net');
    sections.push('- Identify premium domains and estimated acquisition costs');
  }

  sections.push('');
  sections.push('### 5. Cultural & Linguistic Safety');
  sections.push('- Check for unintended meanings in major languages (Spanish, Mandarin, French, German)');
  sections.push('- Flag potential offensive or inappropriate connotations');
  sections.push('- Assess pronunciation challenges for global audiences');
  sections.push('');
  sections.push('### 6. Sentiment & Association Analysis');
  sections.push('- Analyze existing online sentiment if name is used elsewhere');
  sections.push('- Identify any negative news or associations');
  sections.push('- Check for meme/slang usage that could impact brand perception');
  sections.push('');
  sections.push('### Output Format');
  sections.push('');
  sections.push('For each favorited name, provide:');
  sections.push('');
  sections.push('1. **Risk Level**: Low / Medium / High');
  sections.push('2. **Key Findings**: Bullet-point summary of critical discoveries');
  sections.push('3. **Recommended Actions**: Specific next steps (e.g., "Consult trademark attorney", "Negotiate domain purchase", "Consider alternative spelling")');
  sections.push('4. **Overall Assessment**: 2-3 sentence recommendation on viability');
  sections.push('');
  sections.push('Prioritize findings that could block or delay brand launch. Include specific URLs, trademark registration numbers, and data sources for verification.');
  sections.push('');

  // Project context footer
  sections.push('---');
  sections.push('');
  sections.push('_This document was generated by Namenym for agentic research with Collabrador. Project brief and favorited names should guide research priorities._');

  return sections.join('\n');
}

export function downloadCollabradorExport(project: NamingProject): void {
  const markdown = generateCollabradorMarkdown(project);
  const slug = slugify(project.name);
  const filename = `${slug}-collabrador.md`;

  const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
